import { useState, type ReactNode } from 'react';
import { CircleAlert, ShieldOff } from 'lucide-react';
import { requiresMfa, type Role } from '@cp/shared';
import { Banner, Button, ConfirmDialog, Drawer, Fact, Facts, StatusPill } from '@/components/ui';
import { RolePicker } from '@/components/users/RolePicker';
import { useResetUserMfa, useSetUserRoles } from '@/hooks/useUsers';
import { toast } from '@/store/toastStore';
import type { UserSummary } from '@/types/api';
import { errorText } from '@/utils/errors';
import { formatDateTime } from '@/utils/format';
import { USER_STATUS } from '@/utils/labels';

/**
 * One account: its roles, and its second factor. Roles are edited in a drawer so
 * the list stays visible: changing what someone may do is usually done by
 * comparing them against everyone else.
 */
export function RolesDrawer({
  user,
  isSelf,
  onClose,
}: {
  readonly user: UserSummary | null;
  readonly isSelf: boolean;
  readonly onClose: () => void;
}): ReactNode {
  // Held after `user` goes null so the closing animation has something to draw.
  const [last, setLast] = useState<UserSummary | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [resetting, setResetting] = useState(false);

  if (user && user !== last) {
    setLast(user);
    setRoles([...user.roles]);
  }

  const data = user ?? last;

  const save = useSetUserRoles({
    onSuccess: () => {
      toast.success('Роли сохранены');
      onClose();
    },
  });

  const reset = useResetUserMfa({
    onSuccess: () => {
      toast.success('Привязка сброшена', 'Сессии этой учётной записи завершены');
      setResetting(false);
      onClose();
    },
  });

  if (!data) return null;

  const mfaRequired = requiresMfa(data.roles);
  const unchanged = roles.length === data.roles.length && roles.every((role) => data.roles.includes(role));

  return (
    <Drawer
      open={user !== null}
      title={data.displayName ?? data.login}
      description={data.email}
      onClose={onClose}
      width="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            Закрыть
          </Button>
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={unchanged || roles.length === 0 || isSelf}
            onClick={() => save.mutate({ id: data.id, roles })}
          >
            Сохранить роли
          </Button>
        </>
      }
    >
      <div className="stack stack-loose">
        <Facts>
          <Fact label="Логин">
            <span className="mono">{data.login}</span>
          </Fact>
          <Fact label="Доступ">
            <StatusPill map={USER_STATUS} status={data.status} />
          </Fact>
          <Fact label="Второй фактор">
            {data.mfaEnabled ? (
              <span className="row-tight">
                привязан
                {isSelf ? null : (
                  <Button size="sm" variant="ghost" icon={ShieldOff} onClick={() => setResetting(true)}>
                    Сбросить
                  </Button>
                )}
              </span>
            ) : mfaRequired ? (
              <span className="tone-degraded">обязателен, привяжет при следующем входе</span>
            ) : (
              'не привязан'
            )}
          </Fact>
          <Fact label="Создан">{formatDateTime(data.createdAt)}</Fact>
          <Fact label="Последний вход">{data.lastLoginAt ? formatDateTime(data.lastLoginAt) : 'ни разу'}</Fact>
        </Facts>

        {isSelf ? (
          <Banner tone="info" icon={CircleAlert}>
            Собственные роли изменить нельзя.
          </Banner>
        ) : null}

        <RolePicker value={roles} onChange={setRoles} disabled={isSelf} />

        {save.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(save.error, 'Не удалось сохранить роли')}
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        open={resetting}
        title="Сбросить второй фактор?"
        description={`Учётная запись ${data.login} потеряет привязку к текущему устройству, а её сессии завершатся.${
          mfaRequired ? ' При следующем входе будет предложено привязать новое.' : ''
        }`}
        confirmLabel="Сбросить"
        destructive
        busy={reset.isPending}
        error={reset.isError ? errorText(reset.error, 'Не удалось сбросить привязку') : undefined}
        onConfirm={() => reset.mutate(data.id)}
        onClose={() => setResetting(false)}
      />
    </Drawer>
  );
}
